// The apply-only list (spec 16 §4, D-305 rulings 20 and 21). More views opens
// it at every width: a Modal holding one button per view, in the chip row's
// order, with the other members' shared views under the same "Shared" label
// as the row. Each button applies its view and closes the list. It offers no
// rename, share or delete, so it is the same list on a 412 px phone, where
// Manage is absent, and on a 1440 px desktop, and every view the reader can
// see stays reachable beyond the chip budget.
//
// THE APPLIED VIEW (W8, PR-E4 wave 5). The button of the view the address
// names carries `aria-current="true"` and a quiet "Applied" note beside it
// (`views.more.applied`), so a reader who opens the list over an applied view
// sees which one is on before choosing another. Nothing else marks a row.

import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Modal } from "../../../../components/modal";
import { viewLabel } from "../lib/view-label";
import type { SavedView, ViewGroups } from "../lib/view-state";

export interface ApplyViewsModalProps {
  readonly groups: ViewGroups;
  /** The view the address names, marked as applied. */
  readonly activeId: string | undefined;
  readonly onApply: (view: SavedView) => void;
  readonly onClose: () => void;
}

export function ApplyViewsModal({ groups, activeId, onApply, onClose }: ApplyViewsModalProps) {
  const { t } = useTranslation();
  const sharedLabelId = useId();

  const item = (view: SavedView) => (
    <div key={view.id} role="listitem" className="views-row">
      <Button
        size="sm"
        className="views-apply"
        data-view={view.id}
        data-testid={`views-apply-${view.id}`}
        {...(view.id === activeId ? { "aria-current": "true" as const } : {})}
        onClick={() => {
          onApply(view);
          onClose();
        }}
      >
        {viewLabel(view, t)}
      </Button>
      {view.id === activeId ? (
        <span className="views-row-meta">{t("views.more.applied")}</span>
      ) : null}
    </div>
  );

  return (
    <Modal
      label={t("views.more.title")}
      heading={t("views.more.title")}
      closeLabel={t("views.more.close")}
      onClose={onClose}
      className="views-modal"
      testId="views-apply-list"
    >
      <div role="list" className="views-list">
        {[...groups.system, ...groups.own].map(item)}
      </div>
      {groups.shared.length > 0 ? (
        <div role="group" aria-labelledby={sharedLabelId} data-group="shared">
          <p id={sharedLabelId} className="views-list-label">
            {t("views.shared")}
          </p>
          <div role="list" className="views-list">
            {groups.shared.map(item)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
